import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowLeft, ChevronDown } from "lucide-react";
import { SEEDKEEPERS_URL } from "@/lib/api";
import { LOGO } from "@/lib/logo";

const GROUPS = [
  { label: "Our Approach", to: "/approach" },
  {
    label: "Learn",
    children: [
      { to: "/courses", label: "Courses" },
      { to: "/on-country", label: "On-Country Learning" },
      { to: "/library", label: "Resources" },
    ],
  },
  {
    label: "Services",
    children: [
      { to: "/seed-banks", label: "Seed Banks" },
      { to: "/organisations", label: "For Organisations" },
    ],
  },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const linkBase = "text-[0.85rem] tracking-wide transition-colors text-cream/90 hover:text-cream";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl border-b border-cream/10 ${
        scrolled ? "bg-charcoal/95" : "bg-charcoal/80"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0 text-cream">
          <img src={LOGO.markCream} alt="SeedKeepers Academy" className="h-10 w-10 object-contain" />
          <span className="font-serif text-xl leading-none tracking-tight hidden sm:block">
            SeedKeepers
            <span className="block text-[0.55rem] tracking-[0.35em] uppercase font-sans font-semibold opacity-70 mt-0.5">Academy</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {GROUPS.map((g, i) =>
            g.to ? (
              <NavLink
                key={g.label}
                to={g.to}
                data-testid={`nav-${g.to.slice(1)}`}
                className={({ isActive }) => `${linkBase} ${isActive ? "underline underline-offset-8 decoration-ochre" : ""}`}
              >
                {g.label}
              </NavLink>
            ) : (
              <div
                key={g.label}
                className="relative"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <button data-testid={`nav-group-${g.label.toLowerCase()}`} className={`${linkBase} flex items-center gap-1`}>
                  {g.label}
                  <ChevronDown size={14} className={`transition-transform ${hover === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {hover === i && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full pt-3 min-w-[220px]"
                    >
                      <div className="bg-charcoal/95 backdrop-blur-xl border border-cream/10 py-2">
                        {g.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-${c.to.slice(1)}`}
                            className={({ isActive }) => `block px-5 py-3 text-sm text-cream/85 hover:text-cream hover:bg-cream/5 transition-colors ${isActive ? "text-ochre" : ""}`}
                          >
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-5 shrink-0">
          <a href={SEEDKEEPERS_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-visit-seedkeepers" className="group flex items-center gap-1.5 text-xs tracking-wide text-cream/70 hover:text-cream transition-colors">
            <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-1" />
            Visit SeedKeepers
          </a>
          <Link to="/courses" data-testid="nav-explore-btn" className="bg-ochre text-cream px-6 py-3 text-sm hover:bg-copper transition-colors">
            Explore the Academy
          </Link>
        </div>

        <button onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" className="lg:hidden text-cream" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-charcoal border-t border-cream/10 px-6 py-8 flex flex-col gap-5 max-h-[85vh] overflow-y-auto text-cream" data-testid="nav-mobile-menu">
          {GROUPS.map((g) =>
            g.to ? (
              <Link key={g.label} to={g.to} className="text-lg font-serif" data-testid={`nav-mobile-${g.to.slice(1)}`}>
                {g.label}
              </Link>
            ) : (
              <div key={g.label}>
                <p className="overline text-cream/40 mb-3">{g.label}</p>
                <div className="flex flex-col gap-3 pl-2">
                  {g.children.map((c) => (
                    <Link key={c.to} to={c.to} className="text-lg font-serif" data-testid={`nav-mobile-${c.to.slice(1)}`}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            )
          )}
          <Link to="/courses" className="bg-ochre text-cream px-6 py-3 text-center mt-2" data-testid="nav-mobile-explore">
            Explore the Academy
          </Link>
          <a href={SEEDKEEPERS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cream/60 text-sm">
            <ArrowLeft size={14} /> Visit SeedKeepers
          </a>
        </div>
      )}
    </header>
  );
}
